import { useState, useEffect } from 'react'

import { getExampleNumber, parsePhoneNumberWithError } from 'libphonenumber-js'
import i18nIsoCountries from 'i18n-iso-countries'
import enCountries from 'i18n-iso-countries/langs/en.json'
import PhoneInput from 'react-phone-number-input/input'
import examples from 'libphonenumber-js/mobile/examples'
import { Input } from '../ui/input'
import { ComboboxCountryInput } from './combobox'
import { getCountriesOptions, isoToEmoji, replaceNumbersWithZeros } from './helpers'

i18nIsoCountries.registerLocale(enCountries)

const PhoneInputWithCountry = ({ phoneNumber, setPhoneNumber }) => {
  const options = getCountriesOptions()

  // Default country based on a sample number or provided countryCode
  const getInitialCountry = () => {
    // If phoneNumber is provided, try to parse it to get the country
    if (phoneNumber) {
      try {
        const parsed = parsePhoneNumberWithError(phoneNumber, {})
        if (parsed && parsed.country) {
          return options.find((option) => option.value === parsed.country)
        }
      } catch (error) {
        console.log('Error parsing phone number:', error)
      }
    }

    // Fall back to provided countryCode or default
    const defaultCountry = parsePhoneNumberWithError(`+91606060606`)?.country
    const targetCountry = defaultCountry
    return options.find((option) => option.value === targetCountry) || options[0]
  }

  const [country, setCountry] = useState(getInitialCountry())

  // Update country when phoneNumber changes externally
  useEffect(() => {
    if (phoneNumber) {
      try {
        const parsed = parsePhoneNumberWithError(phoneNumber, {})
        if (parsed && parsed.country) {
          const newCountry = options.find((option) => option.value === parsed.country)
          if (newCountry && newCountry.value !== country.value) {
            setCountry(newCountry)
          }
        }
      } catch (error) {
        console.log('Error parsing phone number in useEffect:', error)
      }
    }
  }, [phoneNumber, options, country.value])

  const placeholder = replaceNumbersWithZeros(
    getExampleNumber(country.value, examples)?.formatInternational(),
  )

  const onCountryChange = (value) => {
    setPhoneNumber(undefined)
    setCountry(value)
  }

  return (
    <div className='not-prose mt-2 flex w-full min-w-0 flex-col gap-4'>
      <div className='flex w-full min-w-0 items-stretch gap-2'>
        <div className='flex-shrink-0'>
          <ComboboxCountryInput
            value={country}
            onValueChange={onCountryChange}
            options={options}
            placeholder='Find your country...'
            renderOption={({ option }) => `${isoToEmoji(option.value)} ${option.label}`}
            renderValue={(option) => option.label}
            emptyMessage='No country found.'
          />
        </div>
        <div className='min-w-0 flex-1'>
          <PhoneInput
            international
            withCountryCallingCode
            country={country.value.toUpperCase()}
            value={phoneNumber}
            inputComponent={Input}
            placeholder={placeholder}
            onChange={(value) => {
              setPhoneNumber(value)
            }}
            className='w-full min-w-0 border'
          />
        </div>
      </div>
    </div>
  )
}

export default PhoneInputWithCountry
